export type ExperimentName = `experiment.${string}`
type Experiment = {
  defaultValue: 'noVariation'
  urlPatterns: Array<string>
}

/**
 * This dictionary holds the configuration for all the experiments currently
 * being assigned by the experiments worker.
 *
 * @property defaultValue - Must be set to noVariation for all of them.
 * @property urlPatterns - Allows you to scope each experiment to their
 * associated set of pages. Those patterns are used on the worker side
 * to know if the experiment will be used to process the current request
 * or not. It helps us improving our cache hit ratio, and thus the
 * overall server-side performance.
 */
export const config = {
  'experiment.globalAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.homeAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/?$' /* Home page */],
  },
  'experiment.landingAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* Landing page */],
  },
  'experiment.productAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.checkoutAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  /** Variations:
   * home
   * home-duplicated */
  'experiment.webHomeCMSPageName': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/?$' /* Home page - permanent AB test */],
  },
  'experiment.goodDealsEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - GoodDeal */,
    ],
  },
  'experiment.recyclingEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - Recycling */,
    ],
  },
  'experiment.holidaysEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - Holidays */,
    ],
  },
  'experiment.homeBuybackPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages - permanent AB test */,
    ],
  },
  'experiment.smartphonesUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Smartphones */,
    ],
  },
  'experiment.laptopsUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Laptops */,
    ],
  },
  'experiment.tabletsUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Tablets */,
    ],
  },
  'experiment.audioUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Audio */,
    ],
  },
  'experiment.gamingUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Gaming */,
    ],
  },
  'experiment.appliancesUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Appliances */,
    ],
  },
  'experiment.macbookPlpPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/apple-macbook/.*' /* Macbook PLP - permanent AB test */,
    ],
  },
  'experiment.ipadPlpPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/apple-ipad/.*' /* iPad PLP - permanent AB test */,
    ],
  },
  // https://backmarket.atlassian.net/wiki/spaces/SR/pages/5334073749/PRD+Gift+Cards+POC?focusedCommentId=5750030399
  'experiment.giftCards': {
    defaultValue: 'noVariation',
    /** Variations:
     * withGiftCards
     * withoutGiftCards */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.enableScalapayIT': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages (in the footer) */],
  },
  'experiment.reviewRelevanceAlgorithm': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * relevanceBaseline
     * relevanceV2 */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
      '^/[a-z]{2}-[a-z]{2}/r/.*' /* Reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/r/s/.*' /* Seller Reviews pages */,
    ],
  },
  'experiment.reviewRelevanceAlgorithmPPGallery': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * relevanceBaseline
     * relevanceV2 */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.reviewSellerAndFilters': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * showSeller */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
      '^/[a-z]{2}-[a-z]{2}/r/p/.*' /* Product Reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/r/.*' /* Reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/r/s/.*' /* Seller Reviews pages */,
    ],
  },
  'experiment.reviewAmount': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * tenReviews
     * sevenReviews */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
    ],
  },
  'experiment.reviewsAiSummary': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withoutAIReview
     * withAIReview
     */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP */,
    ],
  },
  'experiment.reviewsNewAITags': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withoutAITags
     * withAITags
     * withHighlightedAITags
     */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP */,
    ],
  },
  'experiment.reviewsHelpRequestStatus': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withoutHelpRequestStatus
     * withHelpRequestStatus
     */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
      '^/[a-z]{2}-[a-z]{2}/r/.*' /* All reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/s/.*' /* Seller pages */,
    ],
  },
  'experiment.reviewsReviewerInformation': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
      '^/[a-z]{2}-[a-z]{2}/r/.*' /* Reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/s/.*' /* Seller pages */,
    ],
    /** Variations:
     * noVariation
     * withReviewerInformation
     * withoutReviewerInformation
     */
  },
  'experiment.sellerRatingPDP': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
    /** Variations:
     * noVariation
     * withSellerRating
     * withoutSellerRating
     */
  },
  'experiment.reviewsRatingHeader': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withReviewsRatingHeader
     * withoutReviewsRatingHeader
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.usModularPDP': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * enable
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.expressCheckout': {
    /** Variations:
     * enabled
     * disabled
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.applePayOnThirdPartyBrowsers': {
    /** Variations:
     * enabled
     * disabled
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.expressDeliveryBenefit': {
    /** Variations:
     * disabled
     * getItSooner
     * fastestOptionOnly
     */
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */,
    ],
  },
  'experiment.checkoutCollectionPointOption': {
    /** Variations:
     * collectionPointEnabled
     * collectionPointDisabled
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.checkoutServiceExtraction': {
    /** Variations:
     * useBadoom
     * useCheckoutService
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.serviceFeeGrid': {
    /** Variations:
     * newServiceFeeGrid
     * currentServiceFeeGrid
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.cartCutOffCountdown': {
    /** Variations:
     * disabled
     * enabled
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.countrySwitchRedirection': {
    /** Variations:
     * withRedirection
     * withoutRedirection */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.deliveryDateAdjustment': {
    /** Variations:
     * withAdjustment
     * withoutAdjustment */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.flashSales': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.flashSalesOnPDP': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.newOfferPageV3': {
    defaultValue: 'noVariation',
    /** Variations:
     * withGraph
     * withoutGraph */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages */],
  },
  'experiment.resaleBankStep': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBankStep
     * withoutBankStep */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages */],
  },
  'experiment.resaleImeiStepV2': {
    defaultValue: 'noVariation',
    /** Variations:
     * withMandatoryImei
     * withOptionalImei
     * withoutImei
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages */],
  },
  'experiment.buybackNpm': {
    defaultValue: 'noVariation',
    /** Variations:
     * withNpm
     * withOutNpm */
    urlPatterns: ['^.*' /* All pages and applications */],
  },
  'experiment.buybackNpmCustomer': {
    defaultValue: 'noVariation',
    /** Variations:
     * withNpm
     * withOutNpm */
    urlPatterns: ['^.*' /* All pages and applications */],
  },
  'experiment.promoCodeInCart': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.hidePlpBanner': {
    defaultValue: 'noVariation',
    /** Variations:
     * with
     * without */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */],
  },
  'experiment.isBatteryEducationEnabled': {
    defaultValue: 'noVariation',
    /** Variations:
     * with
     * without */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */],
  },
  'experiment.ppJapanBlackFridaySpecial': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBlackFridaySpecial
     * withoutBlackFridaySpecial */
    urlPatterns: ['^/ja-jp/p/.*' /* Product page */],
  },
  'experiment.ppJapanBatteryPickerUpdated': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBatteryPickerUpdated
     * withoutBatteryPickerUpdated */
    urlPatterns: ['^/ja-jp/p/.*' /* Product page */],
  },
  'experiment.ppJapanPremiumDefinitionUpdated': {
    defaultValue: 'noVariation',
    /** Variations:
     * withPremiumDefinitionUpdated
     * withoutPremiumDefinitionUpdated */
    urlPatterns: ['^/ja-jp/p/.*' /* Product page */],
  },
  'experiment.tradeInAU': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/en-au/p/.*' /* Product page */],
  },
  'experiment.tradeInJP': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/ja-jp/.*' /* All JP pages */],
  },
  'experiment.ppUpgradeForLessV2': {
    defaultValue: 'noVariation',
    /** Variations:
     * withUpgrade
     * withoutUpgrade */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.cmsPromotionalBanner': {
    defaultValue: 'noVariation',
    /** Variations:
     * controlText
     * variantText
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages (above Header) */],
  },
  'experiment.ppTelcoCalculator': {
    defaultValue: 'noVariation',
    /** Variations:
     * withCalculator
     * withouCalculator */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  // https://backmarket.atlassian.net/browse/USB2CS-903
  'experiment.ppTelcoComparison': {
    defaultValue: 'noVariation',
    /** Variations:
     * withPicker
     * noVariation */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  // https://backmarket.atlassian.net/browse/USB2CS-916
  'experiment.ppTelcoPartnerPriority': {
    defaultValue: 'noVariation',
    /** Variations:
     * withVisiblePriority
     * withUSMobilePriority
     * withNoblePriority
     * noVariation */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppNobleNewOffer': {
    defaultValue: 'noVariation',
    /** Variations:
     * withNewOffer
     * noVariation */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  // https://backmarket.atlassian.net/browse/CHIPS-980
  'experiment.ppFreenetMobilePlans': {
    defaultValue: 'noVariation',
    /** Variations:
     * withFreenetMobilePlans
     * withoutFreenetMobilePlans
     * noVariation */
    urlPatterns: ['^/de-de/p/.*' /* DE Product page */],
  },
  'experiment.ppAutomatedReviewsInCarousel': {
    defaultValue: 'noVariation',
    /** Variations:
     * withAutomatedReviews
     * withoutAutomatedReviews */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppBatteryConfidence': {
    defaultValue: 'noVariation',
    /** Variations:
     * default
     * withText
     * withNumbers */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */,
    ],
  },
  // https://backmarket.atlassian.net/browse/PPI-7953
  'experiment.ppOptimisedFakeSoldOut': {
    defaultValue: 'noVariation',
    /** Variations:
     * withOptimisedFakeSoldOut
     * withoutOptimisedFakeSoldOut */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppProfessionallyCleanedSticker': {
    defaultValue: 'noVariation',
    /** Variations:
     * withProfessionallyCleaned
     * withoutProfessionallyCleaned */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppCondensedFunnel': {
    defaultValue: 'noVariation',
    /** Variations:
     * withCondensedFunnel
     * withoutCondensedFunnel */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.enableEsUsContent': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.enableEsUsLocaleSelector': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.inputRedesign': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.ppUnitSold': {
    defaultValue: 'noVariation',
    /** Variations:
     * withUnitSold
     * withoutUnitSold */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.plpDuplicatedAlgoliaCalls': {
    defaultValue: 'noVariation',
    /** Variations:
     * withDuplicatedCalls
     * withoutDuplicatedCalls */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP */],
  },
  'experiment.algoliaCardLinks': {
    defaultValue: 'noVariation',
    /** Variations:
     * withDefaultLinks
     * withVariantLinks */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.precisionGradingV3': {
    defaultValue: 'noVariation',
    /** Variations:
     * with
     * without */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.newOfferV3': {
    defaultValue: 'noVariation',
    /** Variations:
     * without
     * withNewOfferA
     * withNewOfferB */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.ppPriceOpportunity': {
    defaultValue: 'noVariation',
    /** Variations:
     * withPriceOpportunity
     * withoutPriceOpportunity */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.postalCodePromise': {
    defaultValue: 'noVariation',
    /** Variations:
     * postalCodeEnabled
     * postalCodeDisabled
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.deliveryPromiseDisplay': {
    defaultValue: 'noVariation',
    /** Variations:
     * deliveryPromiseDateRange
     * deliveryPromiseDaysCount */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */,
    ],
  },
  'experiment.plpWideContainer': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withWideContainer
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */],
  },
  'experiment.backMarketPromise': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBackMarketPromise
     * withoutBackMarketPromise */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.refurbProcess': {
    defaultValue: 'noVariation',
    /** Variations:
     * withRefurbProcess
     * withoutRefurbProcess */
    urlPatterns: ['^/(en|es)-us/p/.*' /* Product page */],
  },
  // https://backmarket.atlassian.net/wiki/spaces/IRS/pages/6016303277/PRD+Annual+recurring+subscriptions
  'experiment.annualRecurring': {
    defaultValue: 'noVariation',
    /** Variations:
     * withAnnualRecurring
     * withoutAnnualRecurring */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.insurancePricingSmartphone': {
    defaultValue: 'noVariation',
    /** Variations:
     * withInsurancePricingSmartphone
     * withoutInsurancePricingSmartphone */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2.*' /* Checkout pages */],
  },
  'experiment.usNewBattery': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBatteryPicker
     * withoutBatteryPicker */
    urlPatterns: ['^/[a-z]{2}-us/p/.*' /* Product page */],
  },
  'experiment.ppJapanHistoricalPrice': {
    defaultValue: 'noVariation',
    /** Variations:
     * withHistoricalPrice
     * withoutHistoricalPrice */
    urlPatterns: ['^/ja-jp/p/.*' /* JP Product page */],
  },
  'experiment.shoppingAssistantWeb': {
    defaultValue: 'noVariation',
    /** Variations:
     * withAssistant
     * QA gate for the web shopping assistant — targeted to an internal
     * audience rather than rolled out by percentage. */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.assistedSearch': {
    defaultValue: 'noVariation',
    /** Variations:
     * withAssistedSearch
     * Pressing Enter in the header search bar both navigates to the search
     * results page and seeds a conversation in the assistant web sidebar.
     * Requires the shopping assistant to be enabled. */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.swapItaly': {
    defaultValue: 'noVariation',
    /** Variations:
     * with
     * without */
    urlPatterns: ['^/it-it/.*' /* All IT pages */],
  },
  // https://backmarket.atlassian.net/wiki/spaces/IRS/pages/6349263236/PRD+Monthly+evergreen+subscriptions+in+the+US?xpis=eyJicmlkZ2UiOiJxdWlja0ZpbmQiLCJpZCI6IjE3ODY1Mjg5MjgzMjciLCJzb3VyY2UiOiJjb25mbHVlbmNlIn0%3D
  'experiment.monthlyEvergreen': {
    defaultValue: 'noVariation',
    /** Variations:
     * withMonthlyEvergreen
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.duolingoPartnership': {
    defaultValue: 'noVariation',
    /** Variations:
     * withDuolino
     * withoutDuolingo */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */,
    ],
  },
  'experiment.annualRecurringIteration': {
    defaultValue: 'noVariation',
    /** Variations:
     * withAnnualRecurringIteration
     * withoutAnnualRecurringIteration */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.jpRefurbishmentInfographic': {
    defaultValue: 'noVariation',
    /** Variations:
     * withJpRefurbishmentInfographic
     * withoutJpRefurbishmentInfographic */
    urlPatterns: ['^/ja-jp/p/.*' /* JP Product page */],
  },
  'experiment.lpoGoogleShoppingConfigurator': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  // https://backmarket.atlassian.net/wiki/spaces/USPM/pages/6566253258/PRD+PLP+Blocks
  'experiment.plpBlocksServicesRevamp': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBlocks
     * withoutBlocks */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.bmProHeaderFRAug26': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBmProHeaderVariant */
    urlPatterns: ['^/fr-fr.*' /* All pages — FR market only */],
  },
  'experiment.bmProHeaderUSAug26': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBmProHeaderVariant */
    urlPatterns: ['^/(en|es)-us.*' /* All pages — US market only */],
  },
  // https://backmarket.atlassian.net/browse/SR-5939
  'experiment.macbookPLPFinder': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */],
  },
  'experiment.selfHostedVideoTypePDP': {
    defaultValue: 'noVariation',
    /** Variations:
     * selfHostedGeneric
     * selfHostedSpecific */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.realtimeCCM': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/bo-staff/customer-care-manager/.*' /* CCM pages */],
  },
  'experiment.plpPaymentAdvertising': {
    defaultValue: 'noVariation',
    /** Variations:
     * withAdvertising
     * withoutAdvertising */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.ppServiceFee': {
    defaultValue: 'noVariation',
    /** Variations:
     * withServiceFee
     * withoutServiceFee */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.signupBm4bCheckboxDisplay': {
    defaultValue: 'noVariation',
    /** Variations:
     * withoutBm4bCheckbox
     * withBm4bCheckbox */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/email/signup.*' /* Email signup page */,
      '^/[a-z]{2}-[a-z]{2}/social/validation.*' /* Social account validation */,
    ],
  },
  'experiment.ppFreeShippingBanner': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBanner
     * withoutBanner */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.photosInReview': {
    defaultValue: 'noVariation',
    /** Variations:
     * withReviewPhotosGuidance */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.reviewPhotosGuidanceDefaultDetails': {
    defaultValue: 'noVariation',
    /** Variations:
     * withNewDefaultDetails */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.vertexAiSearch': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation (default: use Algolia)
     * withVertexAiSearch (use Google Vertex AI) */
    urlPatterns: ['^/fr-fr.*' /* France only */],
  },
  'experiment.priceDropTagAlignement': {
    defaultValue: 'noVariation',
    /** Variations:
     * priceDropTagOriginal - Current purple lowest price in 7 days tag
     * priceDropTagAligned */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.ppATCNewWording': {
    defaultValue: 'noVariation',
    /** Variations:
     * withNewWording
     * withOldWording */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
} satisfies Record<ExperimentName, Experiment>
